/**
 * A person, as initials, and the stack of people currently in a record.
 *
 * The editor's presence display drew its own circles. Initials rather than a
 * photograph because the platform holds no images, and a coloured blank is not
 * a person.
 *
 * @module components/ui/Avatar
 */

import React, { type ReactNode, useState } from "react";

import { Tooltip } from "./Tooltip";

/**
 * Two initials from a display name, or one from a single word.
 *
 * @param name - The person's display name.
 * @returns Up to two uppercase initials, empty when the name is blank.
 */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return "";
  }
  if (words.length === 1) {
    return (words[0]?.slice(0, 1) ?? "").toUpperCase();
  }
  return ((words[0]?.slice(0, 1) ?? "") + (words.at(-1)?.slice(0, 1) ?? "")).toUpperCase();
}

/** The palette an avatar can be drawn in, which is the theme's categorical scale. */
export const AVATAR_COLOURS = [1, 2, 3, 4, 5, 6, 7, 8] as const;
export type AvatarColour = (typeof AVATAR_COLOURS)[number];

/**
 * Written out, not interpolated: Tailwind only generates classes it can read.
 * `text-base-100` is the canvas, which inverts with the theme alongside the
 * lifted dark-mode steps, so the initials keep their contrast in both.
 */
const COLOUR_CLASS: Record<AvatarColour, string> = {
  1: "bg-chart-1 text-base-100 border-transparent",
  2: "bg-chart-2 text-base-100 border-transparent",
  3: "bg-chart-3 text-base-100 border-transparent",
  4: "bg-chart-4 text-base-100 border-transparent",
  5: "bg-chart-5 text-base-100 border-transparent",
  6: "bg-chart-6 text-base-100 border-transparent",
  7: "bg-chart-7 text-base-100 border-transparent",
  8: "bg-chart-8 text-base-100 border-transparent",
};

/**
 * A stable colour for a name, so one person keeps theirs across tabs and
 * sessions. Two people who share a name share a colour, which costs nothing:
 * they already share their initials.
 *
 * @param name - The person's display name.
 * @returns One of {@link AVATAR_COLOURS}.
 */
export function avatarColourFor(name: string): AvatarColour {
  const hash = [...name].reduce(
    (total, character) => (total * 31 + character.charCodeAt(0)) % 0xffffffff,
    0,
  );
  return AVATAR_COLOURS[hash % AVATAR_COLOURS.length];
}

export interface AvatarProps {
  name: string;
  /** What a screen reader hears where the name alone is not enough. */
  label?: string;
  size?: "sm" | "md";
  /** A photograph. Initials stand in where there is none or it fails to load. */
  src?: string;
  /** Tints the initials disc from the categorical scale; plain Surface when unset. */
  colour?: AvatarColour;
  /** A mark on the lower right, such as an activity icon. Decorative. */
  badge?: ReactNode;
  /** For an avatar inside a control that already carries the name. */
  decorative?: boolean;
  className?: string;
  "data-testid"?: string;
}

/** One person. The name reaches a screen reader; the initials are decoration. */
export function Avatar({
  name,
  label = undefined,
  size = "md",
  src = undefined,
  colour = undefined,
  badge = undefined,
  decorative = false,
  className = "",
  "data-testid": testId,
}: AvatarProps): React.ReactElement {
  // The src that failed, so a different one is tried afresh.
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);
  const box = size === "sm" ? "size-6 text-xs" : "size-8 text-xs";
  const named = label ?? name;
  const face =
    colour === undefined ? "border-base-300 bg-base-200 text-base-content" : COLOUR_CLASS[colour];
  const showPhoto = src !== undefined && failedSrc !== src;
  return (
    <span
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : named}
      aria-hidden={decorative ? true : undefined}
      title={decorative ? undefined : named}
      className={`relative inline-flex shrink-0 items-center justify-center rounded-full border font-bold ${face} ${box} ${className}`.trim()}
      data-testid={testId}
    >
      {showPhoto ? (
        <img
          src={src}
          alt=""
          className="size-full rounded-full object-cover"
          onError={() => setFailedSrc(src)}
        />
      ) : (
        <span aria-hidden="true">{initialsOf(name)}</span>
      )}
      {badge ? (
        <span
          aria-hidden
          className="absolute -right-0.5 -bottom-0.5 flex size-3.5 items-center justify-center rounded-full border border-base-300 bg-base-100 text-[0.55rem] text-muted"
        >
          {badge}
        </span>
      ) : null}
    </span>
  );
}

export interface PresencePerson {
  /** Stable across renders; a connection id rather than a name. */
  key: string;
  name: string;
  /** What the tooltip and a screen reader say, such as "Ada is editing Maths". */
  label?: string;
  src?: string;
  colour?: AvatarColour;
  badge?: ReactNode;
  "data-testid"?: string;
  "data-activity"?: string;
}

export interface PresenceStackProps {
  people: readonly PresencePerson[];
  /** Beyond this, the rest become a count. */
  max?: number;
  /** Names the whole group; defaults to a count of people. */
  "aria-label"?: string;
  className?: string;
  "data-testid"?: string;
}

/**
 * Who else is in this record.
 *
 * Overlapped, and capped, because a busy programme would otherwise push the
 * rest of the header off the row. Each person is a focusable control so the
 * tooltip naming them opens for a keyboard as well as a pointer.
 */
export function PresenceStack({
  people,
  max = 4,
  "aria-label": groupLabel = undefined,
  className = "",
  "data-testid": testId,
}: PresenceStackProps): React.ReactElement | null {
  if (people.length === 0) {
    return null;
  }
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  return (
    // `group` so the label is allowed and exposed: ARIA prohibits naming a
    // bare span, so the count reached nobody and the "+2" was read as loose
    // text with no total.
    <span
      role="group"
      className={`flex items-center ${className}`.trim()}
      data-testid={testId}
      aria-label={
        groupLabel ??
        `${String(people.length)} ${people.length === 1 ? "person" : "people"} editing`
      }
    >
      {shown.map((person, index) => {
        const label = person.label ?? person.name;
        return (
          <Tooltip
            key={person.key}
            tip={label}
            placement="bottom"
            className={`hover:z-10 focus-within:z-10 ${index > 0 ? "-ml-2" : ""}`.trim()}
          >
            {/* A button with nothing to do, as `HelpTip` is: focus is what
                opens the tooltip for a keyboard, and only a control may take it. */}
            <button
              type="button"
              className="inline-flex cursor-default rounded-full"
              aria-label={label}
              data-testid={person["data-testid"]}
              data-activity={person["data-activity"]}
            >
              <Avatar
                name={person.name}
                size="sm"
                src={person.src}
                colour={person.colour}
                badge={person.badge}
                decorative
                className="ring-2 ring-base-100"
              />
            </button>
          </Tooltip>
        );
      })}
      {extra > 0 && (
        <span
          aria-hidden
          className="-ml-2 inline-flex size-6 items-center justify-center rounded-full border border-base-300 bg-base-200 text-xs font-bold ring-2 ring-base-100"
        >
          +{extra}
        </span>
      )}
    </span>
  );
}
